import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast, Card } from "@jsq/ds/core";
import { FormSelect, SubmitButton } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { useProcessingPaymentsWithConfirmationStatusLazyQuery } from "~/graphql/supergraphHooks";
import { type PaymentProvider } from "~/graphql/supergraphTypes";
import { getErrorMessage } from "~/utils/errors";
import ProcessingPaymentBatchCard from "./ProcessingPaymentBatchCard";
import * as styles from "./styles_vanilla.css";

interface FormValues {
  providerType: PaymentProvider | "";
}

const validationSchema = Yup.object().shape({
  providerType: Yup.string()
    .required("Provider type is required")
    .oneOf(["MODERN_TREASURY", "FIDES"], "Invalid provider type"),
});

const initialValues: FormValues = {
  providerType: "",
};

const providerTypeOptions = [
  { value: "MODERN_TREASURY", label: "Modern Treasury" },
  { value: "FIDES", label: "FIDES" },
];

const QueryProcessingPayments: React.FC = () => {
  const [getProcessingPayments, { data, loading }] =
    useProcessingPaymentsWithConfirmationStatusLazyQuery();

  const handleSubmit = async (values: FormValues) => {
    if (!values.providerType) {
      toast.error("Please select a provider type");
      return;
    }

    try {
      const result = await getProcessingPayments({
        variables: { providerType: values.providerType },
      });

      if (result.error) {
        toast.error(
          getErrorMessage(result.error) ||
            "An error occurred while querying processing payments",
        );
      } else {
        const resultCount = result.data?.processingPaymentsWithConfirmationStatus?.length || 0;
        const totalPayments =
          result.data?.processingPaymentsWithConfirmationStatus?.reduce(
            (sum, batch) => sum + batch.payments.length,
            0,
          ) || 0;
        toast.success(
          `Found ${resultCount} payment batch(es) with ${totalPayments} processing payment(s)`,
        );
      }
    } catch (err) {
      toast.error(
        getErrorMessage(err) || "An error occurred while querying processing payments",
      );
    }
  };

  const processingPaymentBatches = data?.processingPaymentsWithConfirmationStatus;

  return (
    <div className={styles.container} data-testid="query-processing-payments-root">
      <Flex flexDirection="column" gap={Sizes.LG}>
        <Text variant="strong-xl">Query Processing Payments</Text>

        <Flex
          flexDirection="column"
          gap={Sizes.LG}
          data-testid="query-processing-payments-content"
        >
          <Card>
            <Flex flexDirection="column" gap={Sizes.MD}>
              <Text variant="strong-lg">Select Payment Provider</Text>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <Flex flexDirection="column" gap={Sizes.MD}>
                      <FormSelect
                        name="providerType"
                        label="Provider Type"
                        placeholder="Select a provider type"
                        style={{ maxWidth: "500px" }}
                        full
                        required
                      >
                        <FormSelect.Section>
                          {providerTypeOptions.map((option) => (
                            <FormSelect.Option key={option.value} value={option.value}>
                              {option.label}
                            </FormSelect.Option>
                          ))}
                        </FormSelect.Section>
                      </FormSelect>
                      <SubmitButton
                        isLoading={loading || isSubmitting}
                        style={{ minWidth: "200px" }}
                      >
                        Query Processing Payments
                      </SubmitButton>
                    </Flex>
                  </Form>
                )}
              </Formik>
            </Flex>
          </Card>

          {processingPaymentBatches && (
            <Flex flexDirection="column" gap={Sizes.MD} data-testid="query-results">
              <Text variant="strong-xl">Query Results</Text>
              {processingPaymentBatches.length > 0 ? (
                <Flex flexDirection="column" gap={Sizes.LG}>
                  {processingPaymentBatches.map((batchData) => (
                    <ProcessingPaymentBatchCard
                      key={batchData.paymentBatch.id}
                      batchData={batchData}
                    />
                  ))}
                </Flex>
              ) : (
                <Card>
                  <Flex justifyContent="center">
                    <Text variant="regular-md">
                      No processing payments found for the selected provider
                    </Text>
                  </Flex>
                </Card>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

export default QueryProcessingPayments;
export { QueryProcessingPayments };
